import { type FormEvent, useEffect, useState } from 'react';
import {
  importProxies,
  listProxies,
  retireProxy,
  scanProxies,
  upsertProxy,
  type ProxyInput,
  type RelayConfig,
} from '../api/client';
import type { ProxyCheckStatus, ProxyRow } from '../api/types';
import { IconRefresh } from '../components/Icon';

interface Props {
  cfg: RelayConfig;
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const BLANK_FORM: ProxyInput = {
  id: '',
  label: '',
  server: '',
  username: '',
  password: '',
  bypass: '',
};

export function Proxies({ cfg }: Props) {
  const [rows, setRows] = useState<ProxyRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ProxyInput>(BLANK_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importLabel, setImportLabel] = useState('Webshare');
  const [importScheme, setImportScheme] = useState('http');

  async function refresh() {
    setBusy(true);
    setErr(null);
    try {
      setRows(await listProxies(cfg));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token]);

  function startEdit(row: ProxyRow) {
    setEditingId(row.id);
    setForm({
      id: row.id,
      label: row.label,
      server: row.server,
      username: row.username ?? '',
      password: row.password ?? '',
      bypass: row.bypass ?? '',
    });
    setShowForm(true);
  }

  function startCreate() {
    setEditingId(null);
    setForm(BLANK_FORM);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setForm(BLANK_FORM);
    setEditingId(null);
    setErr(null);
  }

  async function submitForm(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!form.id.trim() || !form.label.trim() || !form.server.trim()) {
      setErr('id, label, and server are required');
      return;
    }
    setBusy(true);
    try {
      const payload: ProxyInput = {
        id: form.id.trim(),
        label: form.label.trim(),
        server: form.server.trim(),
        // Empty strings → null so the Worker writes NULL to D1 rather than ""
        username: form.username?.trim() || null,
        password: form.password?.trim() || null,
        bypass: form.bypass?.trim() || null,
      };
      await upsertProxy(cfg, payload);
      setToast(editingId ? `Updated ${payload.id}` : `Added ${payload.id}`);
      cancelForm();
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  async function submitImport(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!importUrl.trim()) {
      setErr('URL required');
      return;
    }
    setBusy(true);
    try {
      const r = await importProxies(cfg, {
        url: importUrl.trim(),
        label_prefix: importLabel.trim() || undefined,
        scheme: importScheme.trim() || undefined,
      });
      const parts = [`imported ${r.inserted}`];
      if (r.skipped > 0) parts.push(`skipped ${r.skipped}`);
      setToast(parts.join(', '));
      setShowImport(false);
      setImportUrl('');
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function scanAll() {
    setBusy(true);
    setErr(null);
    try {
      const r = await scanProxies(cfg);
      const ok = r.results.filter((x) => x.status === 'ok').length;
      setToast(`scanned ${r.scanned}: ${ok} ok, ${r.scanned - ok} bad`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function scanOne(row: ProxyRow) {
    setBusy(true);
    setErr(null);
    try {
      const r = await scanProxies(cfg, { id: row.id });
      const result = r.results[0];
      if (result) {
        const tail = result.status === 'ok' ? `egress ${result.egress_ip}` : result.error ?? '';
        setToast(`${row.id}: ${result.status}${tail ? ` — ${tail}` : ''}`);
      } else {
        setToast(`${row.id}: no result`);
      }
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function retire(row: ProxyRow) {
    if (!confirm(`Retire proxy ${row.id}? Existing accounts keep using it.`)) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await retireProxy(cfg, row.id);
      setToast(`Retired ${row.id}`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Proxies</h1>
          <p className="text-sm text-[var(--color-text-dim)]">
            Each proxy can register at most one new account per rolling 7-day
            window. Accounts run on local IP when no proxy is eligible.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={busy}
            className="btn-secondary"
          >
            <IconRefresh width={14} height={14} />
            Refresh
          </button>
          <button
            onClick={scanAll}
            disabled={busy}
            className="btn-secondary"
            title="Probe every active proxy through icanhazip.com and persist the result"
          >
            Scan all
          </button>
          <button
            onClick={() => {
              setShowImport(true);
              setShowForm(false);
            }}
            className="btn-secondary"
          >
            Import from URL
          </button>
          <button onClick={startCreate} className="btn-primary">
            Add proxy
          </button>
        </div>
      </header>

      {showImport && (
        <form
          onSubmit={submitImport}
          className="card space-y-3 border border-[var(--color-border)] p-4"
        >
          <div className="text-sm font-semibold tracking-tight">
            Import Webshare list
          </div>
          <p className="text-xs text-[var(--color-text-dim)]">
            The Worker fetches the URL (avoids browser CORS), parses one{' '}
            <code className="text-zinc-300">host:port:user:pass</code> per line,
            and upserts each row keyed on host. Re-importing the same URL just
            refreshes credentials — no duplicates.
          </p>
          <Field
            label="URL"
            hint="e.g. https://proxy.webshare.io/api/v2/proxy/list/download/<token>/-/any/username/direct/-/"
            value={importUrl}
            onChange={setImportUrl}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="label prefix"
              hint="written as `<prefix> <host>`"
              value={importLabel}
              onChange={setImportLabel}
            />
            <Field
              label="scheme"
              hint="http | socks5"
              value={importScheme}
              onChange={setImportScheme}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowImport(false)}
              disabled={busy}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary">
              Import
            </button>
          </div>
        </form>
      )}

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={submitForm}
          className="card space-y-3 border border-[var(--color-border)] p-4"
        >
          <div className="text-sm font-semibold tracking-tight">
            {editingId ? `Edit ${editingId}` : 'New proxy'}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="id"
              hint="short slug, e.g. wbs-72-1-156-111"
              value={form.id}
              disabled={!!editingId}
              onChange={(v) => setForm({ ...form, id: v })}
            />
            <Field
              label="label"
              hint="human-readable"
              value={form.label}
              onChange={(v) => setForm({ ...form, label: v })}
            />
            <Field
              label="server"
              hint="http://1.2.3.4:8001 or socks5://…"
              value={form.server}
              onChange={(v) => setForm({ ...form, server: v })}
              className="col-span-2"
            />
            <Field
              label="username"
              hint="Webshare user (optional)"
              value={form.username ?? ''}
              onChange={(v) => setForm({ ...form, username: v })}
            />
            <Field
              label="password"
              hint="Webshare pass (optional)"
              type="password"
              value={form.password ?? ''}
              onChange={(v) => setForm({ ...form, password: v })}
            />
            <Field
              label="bypass"
              hint="comma-separated bypass hosts (optional)"
              value={form.bypass ?? ''}
              onChange={(v) => setForm({ ...form, bypass: v })}
              className="col-span-2"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={cancelForm}
              disabled={busy}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary">
              {editingId ? 'Save' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {!rows && !err && (
        <div className="text-[var(--color-text-dim)]">Loading…</div>
      )}

      {rows && rows.length === 0 && (
        <div className="card px-4 py-8 text-center text-sm text-[var(--color-text-dim)]">
          No proxies registered yet. Click <strong>Add proxy</strong> above to
          seed one.
        </div>
      )}

      {rows && rows.length > 0 && (
        <section className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-surface-2)]">
              <tr className="text-left text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
                <th className="px-4 py-2">id</th>
                <th className="px-4 py-2">label</th>
                <th className="px-4 py-2">server</th>
                <th className="px-4 py-2">creds</th>
                <th className="px-4 py-2">status</th>
                <th className="px-4 py-2">check</th>
                <th className="px-4 py-2">cooldown</th>
                <th className="px-4 py-2">held by</th>
                <th className="px-4 py-2 text-right">actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[var(--color-border)]/60 last:border-0"
                >
                  <td className="px-4 py-2 font-mono text-xs text-zinc-200">
                    {r.id}
                  </td>
                  <td className="px-4 py-2 text-zinc-300">{r.label}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-300">
                    {r.server}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.username && r.password ? (
                      <span className="text-emerald-300">set</span>
                    ) : (
                      <span className="text-amber-300">missing</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <ProxyStatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-2">
                    <ProxyCheckCell
                      status={r.last_check_status}
                      checkedAt={r.last_checked_at}
                      egressIp={r.last_check_egress_ip}
                      latencyMs={r.last_check_latency_ms}
                      error={r.last_check_error}
                    />
                  </td>
                  <td className="px-4 py-2 text-xs text-[var(--color-text-dim)]">
                    {formatCooldown(r.last_assigned_at)}
                  </td>
                  <td className="px-4 py-2 text-xs text-[var(--color-text-dim)]">
                    {r.last_assigned_username ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => scanOne(r)}
                        disabled={busy}
                        className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-zinc-300 transition hover:border-zinc-500 hover:text-zinc-100 disabled:opacity-50"
                      >
                        Scan
                      </button>
                      <button
                        onClick={() => startEdit(r)}
                        className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-zinc-300 transition hover:border-zinc-500 hover:text-zinc-100"
                      >
                        Edit
                      </button>
                      {r.status !== 'retired' && (
                        <button
                          onClick={() => retire(r)}
                          className="rounded-md border border-rose-500/40 px-2 py-1 text-xs text-rose-200 transition hover:border-rose-500/70 hover:bg-rose-500/10"
                        >
                          Retire
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'password';
  disabled?: boolean;
  className?: string;
}

function Field({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  disabled,
  className,
}: FieldProps) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="input w-full disabled:opacity-50"
      />
      {hint && (
        <span className="mt-1 block text-[10px] text-[var(--color-text-faint)]">
          {hint}
        </span>
      )}
    </label>
  );
}

function ProxyCheckCell({
  status,
  checkedAt,
  egressIp,
  latencyMs,
  error,
}: {
  status: ProxyCheckStatus | null;
  checkedAt: number | null;
  egressIp: string | null;
  latencyMs: number | null;
  error: string | null;
}) {
  if (!status || !checkedAt) {
    return (
      <span className="text-xs text-[var(--color-text-faint)]">never</span>
    );
  }
  const ago = formatAgo(checkedAt);
  const tooltip = [
    `checked ${ago}`,
    egressIp && `egress ${egressIp}`,
    latencyMs != null && `${latencyMs}ms`,
    error && `error: ${error}`,
  ]
    .filter(Boolean)
    .join('\n');
  const palette: Record<ProxyCheckStatus, string> = {
    ok: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
    auth_failed: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
    unreachable: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
    timeout: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    bad_egress: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    error: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
  };
  return (
    <div className="flex flex-col gap-0.5" title={tooltip}>
      <span
        className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-xs ${palette[status]}`}
      >
        {status}
      </span>
      <span className="text-[10px] text-[var(--color-text-faint)]">
        {ago}
        {latencyMs != null && status === 'ok' ? ` · ${latencyMs}ms` : ''}
      </span>
    </div>
  );
}

function formatAgo(ms: number): string {
  const delta = Date.now() - ms;
  if (delta < 0) return 'just now';
  const m = Math.floor(delta / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ProxyStatusBadge({ status }: { status: string }) {
  const cls =
    status === 'active'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
      : 'border-zinc-500/30 bg-zinc-500/10 text-zinc-300';
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${cls}`}
    >
      {status}
    </span>
  );
}

function formatCooldown(lastMs: number | null): string {
  if (!lastMs) return 'never assigned';
  const elapsed = Date.now() - lastMs;
  if (elapsed >= ONE_WEEK_MS) return 'eligible';
  const remaining = ONE_WEEK_MS - elapsed;
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return `cooldown ${days}d ${hours}h`;
}
